import React, { ChangeEvent, forwardRef } from "react";
import styled from "styled-components";

import { FieldOption, resolveOptionLabel } from "./optionLabel";

const StyledTextarea = styled.textarea.attrs({
  className: "bodytext",
})`
  line-height: 150%;
`;

const StyledCounter = styled.div.attrs({
  className: "bodytext-11 u-w100",
})<{ $isOverLimit: boolean }>`
  text-align: right;
  color: ${({ $isOverLimit }) =>
    $isOverLimit ? "var(--soremo-green)" : "var(--soremo-light-gray)"};
`;

export interface TextareaProps {
  /**
   * ラベル文字列。指定すると Textarea が `<div>` でラップされ、ラベル + 入力欄
   * の縦並びレイアウトになる。Input と同じ視覚（`bodytext-13 u-line-height-100
   * u-pl4`）に揃える。未指定なら旧 API 互換で textarea + counter のみ描画する。
   */
  label?: string;
  /**
   * ラベル右脇の補助テキスト。`"required"` → 「必須」（青）、`"optional"` →
   * 「任意」（薄グレー）。旧表記（"必須" / "[必須]" / "任意" / "[任意]"）も
   * `resolveOptionLabel` で正規化するが、新規コードは union 値で渡すこと。
   */
  option?: FieldOption | string;
  /** label を出すラッパーの幅。既定 "100%"。Input と同じ仕様。 */
  width?: string;
  name?: string;
  id?: string;
  rows?: number | string;
  placeholder?: string;
  maxLength?: number;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      option,
      width = "100%",
      name,
      id,
      rows,
      placeholder,
      maxLength,
      value = "",
      onChange,
    },
    ref,
  ) => {
    const { text: optionLabel, className: optionClass } = resolveOptionLabel(option);

    const textareaNode = (
      <StyledTextarea
        name={name}
        id={id}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        ref={ref}
      />
    );

    const counterNode = maxLength ? (
      <StyledCounter $isOverLimit={value.length >= maxLength}>
        {value.length}/{maxLength}
      </StyledCounter>
    ) : null;

    // 後方互換: label 未指定なら旧 Fragment 構造で textarea + counter のみ描画。
    if (!label) {
      return (
        <>
          {textareaNode}
          {counterNode}
        </>
      );
    }

    const containerClass =
      width === "100%"
        ? "u-col u-w100 u-gap4 u-relative"
        : "u-col u-fill u-gap4 u-relative";

    return (
      <div className={containerClass}>
        <label className="bodytext-13 u-line-height-100 u-pl4" htmlFor={id}>
          {label}
          {optionLabel && <span className={optionClass}>{optionLabel}</span>}
        </label>
        {textareaNode}
        {counterNode}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
